/**
 * Feature Toggles Configuration
 * Control which features are enabled/disabled to manage costs and functionality
 */

// Load features from localStorage if available
const loadFeaturesFromStorage = () => {
  try {
    const stored = localStorage.getItem('travelflow-features');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.warn('Failed to load features from localStorage:', error);
  }
  return {};
};

// Initialize features with defaults and localStorage overrides
const storedFeatures = loadFeaturesFromStorage();

export const FEATURES = {
  // Flight tracking features
  FLIGHT_TRACKING_ENABLED: storedFeatures.FLIGHT_TRACKING_ENABLED ?? true, // Enabled by default for test flights
  FLIGHT_TRACKING_AUTO_REFRESH: storedFeatures.FLIGHT_TRACKING_AUTO_REFRESH ?? false, // Disabled to prevent automatic API calls
  
  // Notification features
  NOTIFICATIONS_ENABLED: storedFeatures.NOTIFICATIONS_ENABLED ?? true,
  EMAIL_NOTIFICATIONS_ENABLED: storedFeatures.EMAIL_NOTIFICATIONS_ENABLED ?? false, // Disabled - SMS only
  
  // Other features
  PDF_UPLOAD_ENABLED: storedFeatures.PDF_UPLOAD_ENABLED ?? true,
  INVITE_CODES_ENABLED: storedFeatures.INVITE_CODES_ENABLED ?? true,
  
  // Restaurant booking features
  OPENTABLE_ENABLED: storedFeatures.OPENTABLE_ENABLED ?? true, // Enabled by default, uses mock data until API approval
  OPENTABLE_LIVE_API: storedFeatures.OPENTABLE_LIVE_API ?? false, // Controls live OpenTable API vs AI-fallback; requires OPENTABLE_ENABLED
  
  // Beta access control — 'private' = invite-gated custom flow, 'public' = standard Cognito Authenticator
  // SECURITY: hardcoded — not overridable via localStorage to prevent client-side bypass
  BETA_MODE: 'private',
  
  // Development features
  // SECURITY: hardcoded in production builds — not overridable via localStorage to prevent PII exposure
  DEBUG_MODE: process.env.NODE_ENV === 'development' ? (storedFeatures.DEBUG_MODE ?? false) : false,
  CONSOLE_LOGGING: process.env.NODE_ENV === 'development' ? (storedFeatures.CONSOLE_LOGGING ?? false) : false
};

/**
 * Check if a feature is enabled
 * @param {string} featureName - The feature to check
 * @returns {boolean} Whether the feature is enabled
 */
export const isFeatureEnabled = (featureName) => {
  return FEATURES[featureName] === true;
};

/**
 * Get all feature settings
 * @returns {object} All feature configurations
 */
export const getFeatures = () => {
  return { ...FEATURES };
};

/**
 * Update a feature setting (for runtime changes)
 * @param {string} featureName - The feature to update
 * @param {boolean} enabled - Whether to enable the feature
 */
export const updateFeature = (featureName, enabled) => {
  if (FEATURES.hasOwnProperty(featureName)) {
    FEATURES[featureName] = enabled;
    
    // Persist to localStorage
    try {
      const stored = loadFeaturesFromStorage();
      stored[featureName] = enabled;
      localStorage.setItem('travelflow-features', JSON.stringify(stored));
      
      // Dispatch custom event to notify other components
      window.dispatchEvent(new CustomEvent('featureToggleChanged', { 
        detail: { featureName, enabled } 
      }));
    } catch (error) {
      console.error('Failed to persist feature to localStorage:', error);
    }
  }
};

